import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { getMyGroups } from "@/api/admin-api"
import { Check, ChevronsUpDown } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type LocalAiLeadGroupPickerProps = {
  groupId: string | null
  groupName: string | null
  onChange: (id: string | null, name: string | null) => void
  // Optional overrides so the same picker can back other group settings
  // (e.g. the tender platform-access group) with their own label/copy.
  id?: string
  label?: string
  noGroupLabel?: string
}

function useMyGroupsQuery() {
  return useQuery({
    queryKey: ["graph", "me", "groups"],
    queryFn: getMyGroups,
    staleTime: 1000 * 60 * 10,
  })
}

export function LocalAiLeadGroupPicker({
  groupId,
  groupName,
  onChange,
  id = "local-ai-lead-user-group",
  label,
  noGroupLabel: noGroupLabelOverride,
}: LocalAiLeadGroupPickerProps) {
  const { t } = useTranslation()
  const myGroupsQuery = useMyGroupsQuery()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [manualInput, setManualInput] = useState(false)

  const fieldLabel =
    label ?? t("adminDashboard.localAiLeadSettings.userGroupLabel", "Platform access group")
  const noGroupLabel =
    noGroupLabelOverride ??
    t("adminDashboard.localAiLeadSettings.noGroupSelected", "No group selected (all users)")

  if (manualInput) {
    return (
      <div className="flex-1">
        <div className="space-y-1.5">
          <Label htmlFor={id}>{fieldLabel}</Label>
          <Input
            autoComplete="off"
            className="font-mono text-sm"
            id={id}
            value={groupId ?? ""}
            placeholder={t(
              "adminDashboard.localAiLeadSettings.manualGroupIdPlaceholder",
              "Paste group object ID (GUID)…"
            )}
            onChange={(e) => {
              const v = e.target.value.trim()
              onChange(v || null, null)
            }}
          />
        </div>
        <div className="h-0 overflow-visible">
          <button
            className="text-muted-foreground hover:text-foreground mt-1 text-xs underline"
            type="button"
            onClick={() => setManualInput(false)}
          >
            {t("adminDashboard.localAiLeadSettings.showGroupPicker", "Pick from my groups")}
          </button>
        </div>
      </div>
    )
  }

  const displayLabel = groupId ? (groupName ?? groupId) : noGroupLabel

  return (
    <div className="flex-1">
      <div className="space-y-1.5">
        <Label htmlFor={id}>{fieldLabel}</Label>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              className="w-full justify-between font-normal"
              id={id}
              role="combobox"
              variant="outline"
            >
              <span className="truncate">{displayLabel}</span>
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[calc(100vw-2rem)] max-w-[400px] p-0">
            <Command>
              <CommandInput
                placeholder={t("adminDashboard.localAiLeadSettings.searchGroups", "Search groups…")}
              />
              <CommandList>
                <CommandEmpty>
                  {myGroupsQuery.isLoading
                    ? t("common.loading", "Loading…")
                    : t("adminDashboard.localAiLeadSettings.noGroupsFound", "No groups found")}
                </CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onChange(null, null)
                      setPopoverOpen(false)
                    }}
                  >
                    <Check className={cn("mr-2 size-4", !groupId ? "opacity-100" : "opacity-0")} />
                    <span className="text-muted-foreground italic">{noGroupLabel}</span>
                  </CommandItem>
                  {(myGroupsQuery.data ?? []).map((group) => (
                    <CommandItem
                      key={group.oid}
                      value={`${group.displayName} ${group.oid}`}
                      onSelect={() => {
                        onChange(group.oid, group.displayName)
                        setPopoverOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-4",
                          groupId === group.oid ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{group.displayName}</span>
                        <span className="text-muted-foreground font-mono text-xs">{group.oid}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div className="h-0 overflow-visible">
        <button
          className="text-muted-foreground hover:text-foreground mt-1 text-xs underline"
          type="button"
          onClick={() => setManualInput(true)}
        >
          {t("adminDashboard.localAiLeadSettings.manualGroupIdLink", "Enter group ID manually")}
        </button>
      </div>
    </div>
  )
}
